import React from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaAngular, FaHtml5, FaCss3Alt, FaDocker } from 'react-icons/fa';
import { 
  SiTypescript, 
  SiJavascript, 
  SiDotnet
} from 'react-icons/si';
import './App.css';
import Hero from './components/Hero/Hero';
import ProjectCard from './components/ProjectCard/ProjectCard';
import ProjectDetail from './components/ProjectDetail/ProjectDetail';
import Project from './components/ProjectCard/Project';
import Footer from './components/Footer/Footer';
import DockExample from './components/Dock/DockExample';
import TiltedCard from './components/TiltedCard/TiltedCard';
import { ThemeProvider } from './context/ThemeContext';

const projects = [
  new Project({
    id: '1',
    name: 'Voxdrop',
    description: "I built Voxdrop, a native macOS menu bar app for fast voice-to-text transcription that I use throughout the day. A global hotkey records audio and inserts the transcribed text directly into the frontmost application, with transcription powered by Apple Speech or OpenAI Whisper and optional GPT-4o-mini refinement. The app is written in Swift and SwiftUI, ships via direct distribution with Sparkle auto-updates, and is available at voxdrop.io.",
    technologies: ['Swift', 'SwiftUI', 'macOS', 'Supabase'],
    imageUrl: require('./images/1.jpg'),
    link: 'https://voxdrop.io',
  }),
  new Project({
    id: '2',
    name: 'Developer Portfolio Website',
    description: "Welcome to my developer portfolio. I built this website to create a clear and modern online presence where I can showcase my projects, skills, and experience as a web developer. The site is built with Angular, containerized with Docker, and designed to be fast, clean, and responsive. You're currently browsing it at mtwebstudio.de – feel free to explore my work and get in touch!",
    technologies: ['React', 'Bootstrap', 'Docker', 'Javascript', 'HTML', 'CSS'],
    imageUrl: require('./images/2.jpg'),
    /* imageUrl: 'https://images.unsplash.com/photo-1749303025584-0b4e15e4146b?q=80&w=1688&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', */

  }),
  new Project({
    id: '3',
    name: 'beschriftet',
    description: 'I volunteer as a front-end developer for Beschriftet e.V., a non-profit organization that provides a platform for quickly and easily searching Bible content. In this role, I develop the user-friendly front end using Angular and continuously work on expanding the project. The platform is already available at bibeltr.eu.',
    technologies: ['Angular', 'Docker', 'Typescript', '.Net', 'C#', 'HTML', 'CSS', 'Bootstrap', 'MongoDB'],
    imageUrl: require('./images/3.jpg'),
  }),
  new Project({
    id: '4',
    name: 'Side Business Portfolio Website',
    description: 'I developed a portfolio website for my side business to strengthen my online presence and showcase my work to potential clients. The website was built using Angular and is deployed via Docker. It is available at mtwebstudio.de.',
    technologies: ['Angular', 'Bootstrap', 'Docker', 'Typescript', 'HTML', 'CSS'],
    imageUrl: require('./images/4.jpg'),
  }),
    new Project({
      id: '5',
      name: 'Wetrack',
      description: 'For my final project during vocational training, I developed a time tracking tool that was integrated into an ERP system to optimize time logging for support tickets. The application was built using Angular for the front end and .NET for the back end.',
      technologies: ['Angular', 'Bootstrap', 'Typescript', 'HTML', 'CSS', 'C#', '.Net'],
      imageUrl: require('./images/5.jpg'),
    }),
    new Project({
      id: '6',
      name: 'BigBuy / Shopware Interface',
      description: 'I developed an interface between the wholesaler BigBuy and the e-commerce platform Shopware that enables selected products to be imported and published in Shopware with just a few clicks. This significantly simplified the process. The implementation was done using Angular.',
      technologies: ['Angular', 'Bootstrap', 'Typescript', 'HTML', 'CSS'],
      imageUrl: require('./images/6.jpg'),
    }),
    new Project({
      id: '7',
      name: 'FoodTracker',
      description: 'As part of a school project, I worked with a team to develop an application with an integrated barcode scanner designed to help manage household food items for everyday use. The app was developed using Android Studio.',
      technologies: ['Android Studio', 'Java', 'SQLite'],
      imageUrl: require('./images/7.jpg'),
    }),
];

const Home = () => {
  const TechItem = ({ Icon, text }) => (
    <div className="d-flex align-items-center tech-item">
      <Icon size={24} style={{ color: '#9a9a9a' }} />
      <div className="fw-bold ms-2 tech-text">{text}</div>
    </div>
  );

  const TechInfo = () => (
    <div className="tech-info">
      <TechItem Icon={FaAngular} text="Angular" />
      <TechItem Icon={SiDotnet} text="C#" />
      <TechItem Icon={SiTypescript} text="TypeScript" />
      <TechItem Icon={SiJavascript} text="JavaScript" />
      <TechItem Icon={SiDotnet} text=".NET" />
      <TechItem Icon={FaHtml5} text="HTML" />
      <TechItem Icon={FaCss3Alt} text="CSS" />
      <TechItem Icon={FaDocker} text="Docker" />
    </div>
  );

  return (
    <div className="home-container">
      <Helmet>
        <title>Marco Tissen — Developer Portfolio</title>
        <meta name="description" content="Portfolio of Marco Tissen — Web and macOS developer. Projects with React, Angular, Swift/SwiftUI, .NET and Docker." />
        <link rel="canonical" href="https://marcotissen.com/" />
      </Helmet>
      <div className="home-content">
        <div className="home-main-content">
          <div className="home-layout">
            <div className="tilted-card-wrapper">
              <TiltedCard />
            </div>
            <TechInfo />
          </div>
          <div style={{ width: '100%', maxWidth: '800px' }} className="hero-block">
          <Hero hideLocationInfo={false} />
        </div>
        </div>
      </div>
    </div>
  );
};

const Projects = () => (
    <div className="projects-container">
        <Helmet>
          <title>Projects — Marco Tissen</title>
          <meta name="description" content="Selected projects by Marco Tissen: Voxdrop (macOS), beschriftet, web portfolios and more — React, Angular, Swift, .NET." />
          <link rel="canonical" href="https://marcotissen.com/projects" />
        </Helmet>
        {projects.map(project => (
            <ProjectCard key={project.id} project={project} />
        ))}
    </div>
);

const Craft = () => <h1 style={{ textAlign: 'center', marginTop: '100px' }}>Craft Page</h1>;
const Photos = () => <h1 style={{ textAlign: 'center', marginTop: '100px' }}>Photos Page</h1>;

function ProjectDetailWrapper() {
  const { id } = useParams();
  const project = projects.find(p => p.id === id);
  return <ProjectDetail project={project} />;
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route path="/" element={
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
              <DockExample />
              <main style={{ flex: 1, overflow: 'auto' }}>
                <Home />
              </main>
              <Footer />
            </div>
          } />
         <Route path="/projects" element={
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
              <DockExample />
              <main style={{ flex: 1, paddingTop: '100px', overflow: 'auto' }}>
                <Projects />
              </main>
              <Footer />
            </div>
          } />
          {/*
          <Route path="/project/:id" element={
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <DockExample />
              <main style={{ flex: 1, paddingTop: '100px' }}>
                <ProjectDetailWrapper />
              </main>
              <Footer />
            </div>
          } />
          */}
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App; 